import React from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { cn } from './utils';

interface DurationDisplayProps {
  duration: number | number[];
  className?: string;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'badge' | 'inline';
}

/**
 * DurationDisplay - Clean, Calendly-inspired duration display component
 * 
 * Features:
 * - Handles single or multiple durations elegantly
 * - Proper comma separation and formatting
 * - Multiple display variants
 * - Consistent with design system
 */
const DurationDisplay: React.FC<DurationDisplayProps> = ({
  duration,
  className,
  showIcon = true,
  size = 'md',
  variant = 'default',
}) => {
  // Normalize duration to array
  const durations = Array.isArray(duration) ? duration : [duration];
  
  // Format duration with proper units
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return hours === 1 ? '1 hr' : `${hours} hrs`;
    }
    
    return `${hours}h ${remainingMinutes}m`;
  };
  
  // Create formatted duration string with proper separators
  const formattedDurations = durations
    .map(formatDuration)
    .join(', ');
  
  // Size configurations
  const sizeClasses = {
    sm: {
      text: 'text-xs',
      icon: 'h-3 w-3',
      spacing: 'mr-1',
    },
    md: {
      text: 'text-sm',
      icon: 'h-4 w-4',
      spacing: 'mr-1.5',
    },
    lg: {
      text: 'text-base',
      icon: 'h-5 w-5', 
      spacing: 'mr-2',
    },
  };
  
  // Variant configurations
  const variantClasses = {
    default: 'text-gray-600 flex items-center',
    badge: 'inline-flex items-center px-2.5 py-1 rounded-full text-gray-600 bg-gray-100 font-medium',
    inline: 'text-gray-500 inline-flex items-center',
  };
  
  const config = sizeClasses[size];
  
  return (
    <span 
      className={cn(
        variantClasses[variant],
        config.text,
        className
      )}
      title={durations.length > 1 ? `${durations.length} duration options: ${formattedDurations}` : formattedDurations}
    >
      {showIcon && (
        <ClockIcon 
          className={cn(
            config.icon, 
            config.spacing,
            variant === 'default' ? 'text-gray-400' : 'text-gray-500'
          )} 
        />
      )}
      
      {durations.length > 1 ? (
        <>
          <span className="font-medium">{durations.length}</span>
          <span className="ml-1">duration options</span>
        </>
      ) : (
        formattedDurations
      )}
    </span>
  );
};

export default DurationDisplay;